import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import { getAccessToken } from '../Utils/auth';

export interface TablePersistenceState {
    columnFilters?: any;
    columnVisibility?: any;
    columnOrder?: any;
    columnSizing?: any;
    columnPinning?: any;
    globalFilter?: any;
    sorting?: any;
    density?: any;
    grouping?: any;
    pagination?: any;
    showColumnFilters?: boolean;
    showGlobalFilter?: boolean;
    actionButtonOrder?: string[];
    actionButtonVisibility?: Record<string, boolean>;
    isFullScreen?: boolean;
}

/**
 * Custom hook to manage persistence of Material React Table (MRT) state.
 * Scopes the data by both the current User ID (from JWT) and a Screen ID.
 * 
 * @param screenId Unique identifier for the screen/table to persist
 * @param initialState Default state to use if no persisted state is found
 * @returns loadState, saveState, clearState, and utility functions
 */
export const useTablePersistence = (screenId: string, initialState: TablePersistenceState = {}) => {
    const userId = useMemo(() => {
        const token = getAccessToken();
        if (!token) return 'anonymous';
        try {
            const decoded: any = jwtDecode(token);
            // Try to find a unique identifier in the token payload
            return decoded.user_id || decoded.id || decoded.sub || 'anonymous';
        } catch (e) {
            console.warn('Failed to decode JWT token for persistence scoping', e);
            return 'anonymous';
        }
    }, []);

    const storageKey = useMemo(() => `mrt_persistence_${userId}_${screenId}`, [userId, screenId]);

    // Internal state to track changes and trigger saves
    const [state, setState] = useState<TablePersistenceState>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : initialState;
        } catch (e) {
            console.error('Failed to load table state', e);
            return initialState;
        }
    });

    const isInitialized = useRef(false);

    // Save state whenever it changes
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            return;
        }
        
        try {
            localStorage.setItem(storageKey, JSON.stringify(state));
        } catch (e) {
            console.error('Failed to save table state', e);
        }
    }, [state, storageKey]);

    const resetState = useCallback(() => {
        localStorage.removeItem(storageKey);
        setState(initialState);
    }, [storageKey, initialState]);

    const updateState = useCallback((updates: Partial<TablePersistenceState>) => {
        setState((prev) => ({ ...prev, ...updates }));
    }, []);

    // Individual state setter helpers that can be passed to MRT
    const setColumnFilters = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            columnFilters: typeof updater === 'function' ? updater(prev.columnFilters || []) : updater
        }));
    }, []);

    const setColumnVisibility = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            columnVisibility: typeof updater === 'function' ? updater(prev.columnVisibility || {}) : updater
        }));
    }, []);

    const setColumnOrder = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            columnOrder: typeof updater === 'function' ? updater(prev.columnOrder || []) : updater
        }));
    }, []);

    const setColumnSizing = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            columnSizing: typeof updater === 'function' ? updater(prev.columnSizing || {}) : updater
        }));
    }, []);

    const setColumnPinning = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            columnPinning: typeof updater === 'function' ? updater(prev.columnPinning || {}) : updater
        }));
    }, []);

    const setGlobalFilter = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            globalFilter: typeof updater === 'function' ? updater(prev.globalFilter) : updater
        }));
    }, []);

    const setSorting = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            sorting: typeof updater === 'function' ? updater(prev.sorting || []) : updater
        }));
    }, []);

    const setDensity = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            density: typeof updater === 'function' ? updater(prev.density || 'comfortable') : updater
        }));
    }, []);

    const setGrouping = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            grouping: typeof updater === 'function' ? updater(prev.grouping || []) : updater
        }));
    }, []);

    const setPagination = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            pagination: typeof updater === 'function' ? updater(prev.pagination || { pageIndex: 0, pageSize: 15 }) : updater
        }));
    }, []);

    const setShowColumnFilters = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            showColumnFilters: typeof updater === 'function' ? updater(prev.showColumnFilters ?? true) : updater
        }));
    }, []);

    const setShowGlobalFilter = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            showGlobalFilter: typeof updater === 'function' ? updater(prev.showGlobalFilter ?? true) : updater
        }));
    }, []);

    const setActionButtonOrder = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            actionButtonOrder: typeof updater === 'function' ? updater(prev.actionButtonOrder || []) : updater
        }));
    }, []);

    const setActionButtonVisibility = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            actionButtonVisibility: typeof updater === 'function' ? updater(prev.actionButtonVisibility || {}) : updater
        }));
    }, []);

    const setIsFullScreen = useCallback((updater: any) => {
        setState((prev) => ({
            ...prev,
            isFullScreen: typeof updater === 'function' ? updater(prev.isFullScreen ?? false) : updater
        }));
    }, []);

    return {
        state,
        resetState,
        updateState,
        setColumnFilters,
        setColumnVisibility,
        setColumnOrder,
        setColumnSizing,
        setColumnPinning,
        setGlobalFilter,
        setSorting,
        setDensity,
        setGrouping,
        setPagination,
        setShowColumnFilters,
        setShowGlobalFilter,
        setActionButtonOrder,
        setActionButtonVisibility,
        setIsFullScreen,
        storageKey
    };
};
